import { Check, Image, Music, TicketPercent, Video, X } from "lucide-react";
import type React from "react";
import { useCallback, useMemo } from "react";
import type { CheckpointTask } from "schema/agent";
import { ChatState, useProject } from "@/features/project";
import { getCredits } from "./credit";
import { isImageTask, isMusicTask, isVideoTask } from "./validators";

interface BatchApprovalPanelProps {
  messageId: string;
  checkpoint: CheckpointTask[];
  isLast?: boolean;
}

export const BatchApprovalPanel: React.FC<BatchApprovalPanelProps> = ({
  messageId,
  checkpoint,
  isLast,
}) => {
  const {
    toggleTaskApproval: toggleTask,
    setCheckpointApproval: setAllTasks,
    submitChat,
    chatState,
  } = useProject();

  const isStreaming = chatState !== ChatState.IDLE;

  const selectedCredits = useMemo(() => {
    return checkpoint.reduce((sum, task) => (task.approved ? sum + getCredits(task) : sum), 0);
  }, [checkpoint]);

  const selectedCount = useMemo(() => {
    return checkpoint.filter((task) => task.approved).length;
  }, [checkpoint]);

  const handleSubmit = useCallback(() => submitChat(""), [submitChat]);

  // Interactive batch approval panel
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3 my-2 max-w-md">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-slate-400">
          {isLast ? `${checkpoint.length} items pending approval` : `${checkpoint.length} items`}
        </span>
        {selectedCredits > 0 && !isLast && (
          <span className="flex items-center gap-1 text-xs text-slate-400">
            Total: <TicketPercent size={12} /> {selectedCredits}
          </span>
        )}
      </div>

      <div className="space-y-2 mb-3">
        {checkpoint.map((task, i) => {
          const onToggle = () => toggleTask(messageId, i);

          if (isImageTask(task)) {
            return (
              <CheckpointItem
                key={i}
                icon={<Image size={14} className="text-slate-400" />}
                label="AI Image"
                meta={task.ratio}
                prompt={task.prompt}
                credits={getCredits(task)}
                selected={task.approved}
                isPending={isLast}
                disabled={isStreaming}
                onToggle={onToggle}
              />
            );
          }

          if (isVideoTask(task)) {
            return (
              <CheckpointItem
                key={i}
                icon={<Video size={14} className="text-slate-400" />}
                label="AI Video"
                meta={`${task.ratio} · ${task.duration}s`}
                prompt={task.prompt}
                credits={getCredits(task)}
                selected={task.approved}
                isPending={isLast}
                disabled={isStreaming}
                onToggle={onToggle}
              />
            );
          }

          if (isMusicTask(task)) {
            return (
              <CheckpointItem
                key={i}
                icon={<Music size={14} className="text-slate-400" />}
                label="AI Music"
                meta="30s"
                prompt={task.prompt}
                credits={getCredits(task)}
                selected={task.approved}
                isPending={isLast}
                disabled={isStreaming}
                onToggle={onToggle}
              />
            );
          }

          return null;
        })}
      </div>

      {isLast && (
        <>
          <div className="flex gap-2 mb-3 text-xs">
            <button
              onClick={() => setAllTasks(messageId, true)}
              disabled={isStreaming}
              className="text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-slate-400"
            >
              Select All
            </button>
            <span className="text-slate-600">·</span>
            <button
              onClick={() => setAllTasks(messageId, false)}
              disabled={isStreaming}
              className="text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-slate-400"
            >
              Deselect All
            </button>
          </div>

          <div className="flex gap-2">
            {selectedCount > 0 ? (
              <button
                onClick={handleSubmit}
                disabled={isStreaming}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600 text-white text-xs font-medium transition-colors"
              >
                <Check size={14} />
                <span>Approve</span>
                <span className="text-white/50">({selectedCount})</span>
                <span className="text-white/50">·</span>
                <TicketPercent className="text-white/80" size={12} />
                <span className="text-white/80">{selectedCredits}</span>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isStreaming}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700 text-slate-300 text-xs font-medium transition-colors"
              >
                <X size={14} />
                <span>Reject</span>
                {selectedCount > 0 && <span className="text-slate-400">({selectedCount})</span>}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

interface CheckpointItemProps {
  icon: React.ReactNode;
  label: string;
  meta: string;
  prompt: string;
  credits: number;
  selected?: boolean;
  isPending?: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

const CheckpointItem: React.FC<CheckpointItemProps> = ({
  icon,
  label,
  meta,
  prompt,
  credits,
  selected,
  isPending,
  disabled,
  onToggle,
}) => {
  const isApproved = !isPending && selected;
  const isRejected = !isPending && !selected;
  const textColor = isRejected ? "text-slate-500" : "text-slate-300";
  const isInteractive = isPending && !disabled;

  return (
    <div
      className={`flex items-start gap-2 p-2 rounded transition-colors ${
        !isPending
          ? "bg-slate-900/30 pointer-events-none"
          : disabled
            ? "bg-slate-900/50 opacity-50 cursor-not-allowed"
            : selected
              ? "bg-slate-700/50 cursor-pointer"
              : "bg-slate-900/50 hover:bg-slate-800/50 cursor-pointer"
      }`}
      onClick={isInteractive ? onToggle : undefined}
    >
      {isPending && (
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          disabled={disabled}
          className="mt-0.5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0 disabled:cursor-not-allowed"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs">
          <span className={`font-medium ${textColor}`}>{label}</span>
          <span className="flex items-center gap-1 text-slate-400">
            · <TicketPercent size={10} /> {credits}
          </span>
          <span className="text-slate-500 ml-auto">{meta}</span>
        </div>
        <p className={`text-xs line-clamp-2 mt-1 ${textColor}`}>{prompt}</p>
      </div>
      {isApproved && <Check size={14} className="text-emerald-400 mt-0.5" />}
      {isRejected && <X size={14} className="text-slate-500 mt-0.5" />}
    </div>
  );
};
